import { cva } from "class-variance-authority";
import {
    PieChart as PChart,
    Pie,
    Cell,
    Legend,
    Tooltip,
    Label,
} from "recharts";

import { ChartData } from "../../componentTypes/charts";
import { rawPalette } from "../../styles/theme.css";
import { assertNever } from "../../utils/obj";

import * as theme from "./PieChart.css";
export interface PieChartProps<T> {
    data: Array<ChartData<T>>;
    showLegend: boolean;
    standardColor?: string;
    tooltip?: boolean;
    getActivitiesByStatusUrl: (statusKey: T | null) => string;
}
export function PieChart<T>({
    data,
    showLegend,
    standardColor,
    tooltip,
    getActivitiesByStatusUrl,
}: PieChartProps<T>) {
    const sum = data
        .map((entry) => entry.value)
        .reduce((a, b) => {
            return a + b;
        }, 0);

    const formatTooltip = (value: number, name: string) => {
        return [
            `(${parseFloat(((value / sum) * 100).toFixed(1))}%)`,
            `${value} ${name}`,
        ];
    };

    const isEmpty = !data.length || sum === 0;

    const state: PieChartState = isEmpty ? "empty" : "withData";
    const pieData = getPieData(state, data);

    return (
        <PChart width={420} height={270}>
            <Pie
                data={pieData}
                labelLine={false}
                outerRadius={90}
                fill={standardColor}
                dataKey="value"
                nameKey="label"
                stroke="none"
            >
                {pieData.map((entry, index) => {
                    const isValidStatusKey = entry.statusKey !== null;
                    const url = getActivitiesByStatusUrl(entry.statusKey);
                    return (
                        <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            className={pieCellClass({
                                disabled: !isValidStatusKey,
                            })}
                            onClick={() => {
                                if (isValidStatusKey) {
                                    window.location.href = url;
                                }
                            }}
                        />
                    );
                })}
            </Pie>
            <Label />
            {showLegend && (
                <Legend
                    content={
                        <CustomLegend
                            data={data}
                            getActivitiesByStatusUrl={getActivitiesByStatusUrl}
                        />
                    }
                />
            )}
            {tooltip && !isEmpty && (
                <Tooltip
                    wrapperStyle={{ outline: "none" }}
                    separator=" "
                    formatter={formatTooltip}
                />
            )}
        </PChart>
    );
}

const CustomLegend = <T,>({
    data,
    getActivitiesByStatusUrl,
}: {
    data: ChartData<T>[];
    getActivitiesByStatusUrl: (statusKey: T | null) => string;
}) => {
    return (
        <ul className={theme.legend}>
            {data.map((entry, index) => {
                const isValidStatusKey = entry.statusKey !== null;
                return (
                    <li key={`item-${index}`}>
                        <a
                            href={
                                isValidStatusKey
                                    ? getActivitiesByStatusUrl(entry.statusKey)
                                    : undefined
                            }
                            className={legendItemClass({
                                disabled: !isValidStatusKey,
                            })}
                        >
                            <span
                                className={theme.legendBox}
                                style={{ backgroundColor: entry.color }}
                            />
                            <span>{`${entry.label} (${entry.value})`}</span>
                        </a>
                    </li>
                );
            })}
        </ul>
    );
};

type PieChartState = "empty" | "withData";

function getPieData<T>(
    state: PieChartState,
    data: ChartData<T>[]
): ChartData<T>[] {
    const emptyData: ChartData<T>[] = [
        {
            value: 1,
            label: "",
            color: rawPalette.base.greys[50],
            statusKey: null,
        },
    ];
    switch (state) {
        case "empty":
            return emptyData;
        case "withData":
            return data;
        default:
            return assertNever(state);
    }
}

const pieCellClass = cva(theme.cursor, {
    variants: {
        disabled: {
            true: theme.disabledCursor,
            false: null,
        },
    },
});

const legendItemClass = cva(theme.legendItem, {
    variants: {
        disabled: {
            true: theme.disabledCursor,
            false: null,
        },
    },
});
